<template>
  <div class="schedule-message-container">
    <woot-modal
      :show="isFeatureEnabled && show"
      :on-close="onClose"
      :show-close-button="true"
      :close-on-backdrop-click="true"
    >
      <!-- Header do Modal -->
      <div class="modal-header">
        <div class="header-content">
          <div class="title-group">
            <h1 class="modal-title">{{ $t('SCHEDULE_MESSAGES.TITLE') }}</h1>
          </div>
          <Button
            v-if="selectedConversationId"
            variant="ghost"
            color="slate"
            size="sm"
            class="shrink-0"
            @click="selectedConversationId = null"
          >
            {{ $t('SCHEDULE_MESSAGES.CHANGE_CONVERSATION') }}
          </Button>
        </div>
      </div>

      <!-- Container dos blocos -->
      <div v-if="effectiveConversationId" class="schedule-message">
        <!-- Coluna do Formulário -->
        <div class="form-column">
          <form
            class="schedule-message__form"
            @submit.prevent="handleSubmit"
            @click.stop
          >
            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.TITLE_LABEL') }}</label>
              <input
                v-model="title"
                type="text"
                class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                :placeholder="$t('SCHEDULE_MESSAGES.FORM.TITLE_PLACEHOLDER')"
                required
              />
            </div>

            <!-- Campo de seleção de template -->
            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_LABEL') }}</label>
              <div class="template-selector">
                <select
                  v-model="selectedTemplate"
                  class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                  @change="applyTemplate"
                >
                  <option value="">{{ $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_PLACEHOLDER') }}</option>
                  <option
                    v-for="template in templates"
                    :key="template.id"
                    :value="template"
                  >
                    {{
                      template.title ||
                      template.content.substring(0, 50) + '...'
                    }}
                  </option>
                </select>
                <div v-if="loadingTemplates" class="template-loading">
                  <span class="loading-spinner"></span>
                  <span class="loading-text">{{ $t('SCHEDULE_MESSAGES.FORM.TEMPLATE_LOADING') }}</span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.MESSAGE_LABEL') }}</label>
              <textarea
                v-model="message"
                class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                :placeholder="$t('SCHEDULE_MESSAGES.FORM.MESSAGE_PLACEHOLDER')"
              />
            </div>

            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.ATTACHMENTS_LABEL') }}</label>
              <input
                ref="attachmentsInput"
                type="file"
                multiple
                class="hidden"
                @change="onAttachmentChange"
              />
              <div class="flex items-center gap-2">
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  type="button"
                  @click="$refs.attachmentsInput.click()"
                >
                  <fluent-icon icon="attach" size="14" />
                  {{ $t('SCHEDULE_MESSAGES.FORM.ATTACHMENTS_BUTTON') }}
                </Button>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t('SCHEDULE_MESSAGES.FORM.ATTACHMENTS_HELP') }}
                </span>
              </div>
              <div
                v-if="attachments.length"
                class="mt-2 space-y-1"
              >
                <div
                  v-for="(file, index) in attachments"
                  :key="`${file.name}-${file.size}-${index}`"
                  class="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <span class="min-w-0 truncate">{{ file.name }}</span>
                  <div class="flex shrink-0 items-center gap-2">
                    <span class="text-slate-400">{{ formatFileSize(file.size) }}</span>
                    <button
                      type="button"
                      class="text-slate-400 hover:text-ruby-600"
                      @click="removeAttachment(index)"
                    >
                      {{ $t('SCHEDULE_MESSAGES.FORM.ATTACHMENTS_REMOVE') }}
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else-if="existingAttachments.length"
                class="mt-2 text-xs text-slate-500 dark:text-slate-400"
              >
                {{
                  $t('SCHEDULE_MESSAGES.FORM.ATTACHMENTS_CURRENT', {
                    count: existingAttachments.length,
                  })
                }}
              </div>
            </div>

            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.INBOX_LABEL') }}</label>
              <select
                v-model="selectedInbox"
                class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                required
              >
                <option value="">{{ $t('SCHEDULE_MESSAGES.FORM.INBOX_PLACEHOLDER') }}</option>
                <option
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.DATETIME_LABEL') }}</label>
              <input
                v-model="scheduledTime"
                type="datetime-local"
                class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                required
              />
            </div>

            <div class="form-group">
              <div class="checkbox-group">
                <label class="checkbox-label">
                  <input
                    v-model="isRecurrent"
                    type="checkbox"
                    class="checkbox-input"
                  />
                  <span class="checkbox-text dark:text-slate-100">
                    {{ $t('SCHEDULE_MESSAGES.FORM.RECURRENT_LABEL') }}
                  </span>
                </label>
              </div>
            </div>

            <div v-if="isRecurrent" class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.FORM.FREQUENCY_LABEL') }}</label>
              <select
                v-model="selectedPeriod"
                class="form-input dark:bg-slate-700 dark:text-slate-100 dark:border-slate-600"
                required
              >
                <option value="">{{ $t('SCHEDULE_MESSAGES.FORM.FREQUENCY_PLACEHOLDER') }}</option>
                <option value="every_day">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY') }}</option>
                <option value="every_day_at_time">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY_AT_TIME') }}</option>
                <option value="every_week">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEK') }}</option>
                <option value="every_month">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_MONTH') }}</option>
                <option value="every_weekday">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKDAY') }}</option>
                <option value="every_weekend">{{ $t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKEND') }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="dark:text-slate-100">{{ $t('SCHEDULE_MESSAGES.CONDITIONS.TITLE') }}</label>
              <div class="space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                <label class="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    v-model="conditions.noContactReply"
                    type="checkbox"
                    class="mt-1"
                  />
                  <span>{{ $t('SCHEDULE_MESSAGES.CONDITIONS.NO_CONTACT_REPLY') }}</span>
                </label>
                <label class="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    v-model="conditions.conversationOpen"
                    type="checkbox"
                    class="mt-1"
                  />
                  <span>{{ $t('SCHEDULE_MESSAGES.CONDITIONS.CONVERSATION_OPEN') }}</span>
                </label>
                <label class="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    v-model="conditions.sameKanbanStage"
                    type="checkbox"
                    class="mt-1"
                    :disabled="!currentKanbanItem"
                  />
                  <span>
                    {{ $t('SCHEDULE_MESSAGES.CONDITIONS.SAME_KANBAN_STAGE') }}
                    <span
                      v-if="!currentKanbanItem"
                      class="block text-xs text-slate-400"
                    >
                      {{ $t('SCHEDULE_MESSAGES.CONDITIONS.SAME_KANBAN_STAGE_DISABLED') }}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div class="modal-footer dark:border-slate-600">
              <div class="button-wrapper">
                <Button
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="onClose"
                >
                  {{ $t('SCHEDULE_MESSAGES.CANCEL') }}
                </Button>

                <!-- Botão de Agendar -->
                <Button
                  v-if="!isEditing"
                  variant="solid"
                  color="blue"
                  size="sm"
                  type="submit"
                  :disabled="!canSubmit"
                >
                  {{ $t('SCHEDULE_MESSAGES.BUTTON_LABEL') }}
                </Button>

                <!-- Botão de Salvar alterações -->
                <Button
                  v-else
                  variant="solid"
                  color="teal"
                  size="sm"
                  type="button"
                  :disabled="!canSubmit"
                  @click="updateScheduledMessage"
                >
                  {{ $t('SCHEDULE_MESSAGES.SAVE_CHANGES') }}
                </Button>
              </div>
            </div>
          </form>
        </div>

        <!-- Coluna da Lista de Agendamentos -->
        <div class="scheduled-list-column">
          <div class="scheduled-messages">
            <div class="scheduled-messages-header">
              <div class="header-content">
                <div class="title-group">
                  <h3 class="dark:text-slate-100">
                    {{ $t('SCHEDULE_MESSAGES.LIST.TITLE') }}
                    <span v-if="scheduledMessages.length > 0" class="counter">
                      {{ filteredMessages.length }}
                    </span>
                  </h3>

                  <!-- Filtro apenas se houver mensagens -->
                  <woot-button
                    v-if="scheduledMessages.length > 0"
                    variant="clear"
                    size="small"
                    class="filter-button"
                    @click="openFilterMenu($event)"
                  >
                    <fluent-icon icon="filter" size="16" />
                  </woot-button>
                </div>
              </div>
            </div>

            <!-- Estado vazio -->
            <div v-if="scheduledMessages.length === 0" class="empty-state">
              <div class="empty-state-content">
                <svg
                  width="48"
                  height="48"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="empty-icon"
                >
                  <path
                    d="M17.75 3A3.25 3.25 0 0 1 21 6.25v11.5A3.25 3.25 0 0 1 17.75 21H6.25A3.25 3.25 0 0 1 3 17.75V6.25A3.25 3.25 0 0 1 6.25 3h11.5Zm1.75 5.5h-15v9.25c0 .966.784 1.75 1.75 1.75h11.5a1.75 1.75 0 0 0 1.75-1.75V8.5Zm-11.75 6a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm-4.25-4a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm1.5-6H6.25A1.75 1.75 0 0 0 4.5 6.25V7h15v-.75a1.75 1.75 0 0 0-1.75-1.75Z"
                    fill="currentColor"
                  />
                </svg>
                <p class="empty-text">{{ $t('SCHEDULE_MESSAGES.EMPTY') }}</p>
                <p class="empty-subtext">
                  {{ $t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
                </p>
              </div>
            </div>

            <!-- Lista de mensagens (existente) -->
            <div v-else class="scheduled-messages-list">
              <div
                v-for="message in filteredMessages"
                :key="message.id"
                class="scheduled-message"
              >
                <!-- Cabeçalho do Card -->
                <div
                  class="message-header"
                  @click="toggleMessageExpand(message.id)"
                >
                  <div class="header-content">
                    <div class="title-section">
                      <div class="title-and-badge">
                        <span class="title">{{ message.title }}</span>
                        <span
                          class="status-badge"
                          :class="getStatusClass(message.status)"
                        >
                          <span class="pulse-dot"></span>
                          {{ getStatusText(message.status) }}
                        </span>
                      </div>
                      <fluent-icon
                        :icon="
                          expandedMessages.includes(message.id)
                            ? 'chevron-up'
                            : 'chevron-down'
                        "
                        size="14"
                        class="expand-icon"
                      />
                    </div>

                    <div class="meta-info">
                      <div class="datetime">
                        <fluent-icon icon="calendar" size="12" class="icon" />
                        {{ formatDate(message.scheduled_at) }}
                      </div>
                      <div class="inbox">
                        <fluent-icon icon="chat" size="12" class="icon" />
                        {{ getInboxName(message.inbox_id) }}
                      </div>
                      <!-- Indicador de Recorrência -->
                      <div v-if="message.is_recurrent" class="recurrence">
                        <fluent-icon
                          icon="arrow-clockwise"
                          size="12"
                          class="icon"
                        />
                        <span class="recurrence-text">{{
                          getPeriodText(message.period)
                        }}</span>
                      </div>
                      <div
                        v-if="hasSmartConditions(message)"
                        class="recurrence"
                      >
                        <fluent-icon icon="branch" size="12" class="icon" />
                        <span class="recurrence-text">
                          {{ $t('SCHEDULE_MESSAGES.CONDITIONS.BADGE') }}
                        </span>
                      </div>
                    </div>
                    <div
                      v-if="message.conversation_display_id != null"
                      class="flex items-center gap-2 mt-2 px-3.5 py-1.5 rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs min-h-[1.5rem] w-full max-w-full cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/80 shadow-sm"
                      @click.stop="goToConversation(message)"
                    >
                      <div class="flex items-center gap-1 shrink-0">
                        <fluent-icon icon="chat" size="12" class="text-slate-400" />
                        <span class="text-[10px] text-slate-400 select-all px-0.5">
                          #{{ message.conversation_display_id }}
                        </span>
                      </div>
                      <div class="flex items-center gap-1 min-w-0 flex-1">
                        <span class="truncate font-semibold text-slate-700 dark:text-slate-200">
                          {{ getContactDisplayName(message) }}
                        </span>
                        <span class="text-slate-300 dark:text-slate-600 shrink-0">·</span>
                        <span class="truncate text-slate-500 dark:text-slate-400 shrink-0 max-w-[120px]">
                          {{ getInboxName(message.inbox_id) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Conteúdo Expansível -->
                <div
                  v-show="expandedMessages.includes(message.id)"
                  class="message-expandable"
                >
                  <div class="message-preview">
                    <div class="preview-header">
                      <fluent-icon icon="chat" size="14" class="preview-icon" />
                      <span>{{ $t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}</span>
                    </div>
                    <div class="preview-content">
                      {{ message.message }}
                    </div>
                    <div
                      v-if="message.attachments?.length"
                      class="mt-2 flex flex-wrap gap-1"
                    >
                      <span
                        v-for="attachment in message.attachments"
                        :key="attachment.id"
                        class="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      >
                        <fluent-icon icon="attach" size="12" />
                        <span class="truncate">{{ attachment.filename }}</span>
                      </span>
                    </div>
                  </div>

                  <div class="actions">
                    <woot-button
                      v-if="message.conversation_display_id"
                      variant="clear"
                      size="small"
                      class="action-button open-chat"
                      @click.stop.prevent="goToConversation(message)"
                    >
                      <fluent-icon icon="chat" size="12" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION') }}
                    </woot-button>
                    <woot-button
                      variant="clear"
                      size="small"
                      class="action-button resend"
                      @click.stop.prevent="handleResend(message)"
                    >
                      <fluent-icon icon="arrow-clockwise" size="12" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.RESEND') }}
                    </woot-button>
                    <woot-button
                      variant="clear"
                      size="small"
                      class="action-button edit"
                      @click.stop.prevent="handleEdit(message)"
                    >
                      <fluent-icon icon="edit" size="12" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.EDIT') }}
                    </woot-button>
                    <woot-button
                      variant="clear"
                      size="small"
                      class="action-button delete"
                      @click.stop="deleteMessage(message.id)"
                    >
                      <fluent-icon icon="delete" size="12" />
                      {{ $t('SCHEDULE_MESSAGES.LIST.DELETE') }}
                    </woot-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="p-6">
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {{ $t('SCHEDULE_MESSAGES.SELECT_CONVERSATION') }}
        </label>
        <div ref="conversationSelectorRef" class="relative">
          <div
            class="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            @click="openConversationDropdown"
            tabindex="0"
          >
            <span v-if="selectedConversationForSchedule">
              #{{ selectedConversationForSchedule.display_id || selectedConversationForSchedule.id }} -
              {{
                selectedConversationForSchedule.meta?.sender?.name ||
                selectedConversationForSchedule.meta?.sender?.email ||
                $t('KANBAN.ITEM_CONVERSATION.NO_CONTACT')
              }}
            </span>
            <span v-else class="text-slate-400">
              {{ $t('KANBAN.FORM.CONVERSATION.PLACEHOLDER') }}
            </span>
          </div>
          <teleport to="body">
            <div
              v-if="conversationDropdownOpen"
              :style="conversationDropdownStyle"
              class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-96 overflow-y-auto z-[99999]"
              @mousedown.stop
            >
              <input
                v-model="conversationSearch"
                type="text"
                class="w-full px-3 py-2 mb-2 text-sm border-b border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none text-slate-900 dark:text-slate-100"
                :placeholder="$t('KANBAN.SEARCH_CONVERSATION')"
              />
              <div v-if="loadingConversations" class="px-3 py-4 text-slate-500 text-sm">
                {{ $t('KANBAN.LOADING_CONVERSATION') }}
              </div>
              <div v-else class="max-h-72 overflow-y-auto">
                <div
                  v-for="conversation in filteredConversationsForSelect"
                  :key="conversation.id"
                  class="px-3 py-2 hover:bg-woot-50 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-900 dark:text-slate-100"
                  @click="selectConversationForSchedule(conversation)"
                >
                  #{{ conversation.display_id || conversation.id }} -
                  {{
                    conversation.meta?.sender?.name ||
                    conversation.meta?.sender?.email ||
                    $t('KANBAN.ITEM_CONVERSATION.NO_CONTACT')
                  }}
                </div>
                <div
                  v-if="filteredConversationsForSelect.length === 0"
                  class="px-3 py-2 text-slate-400 text-sm"
                >
                  {{ $t('KANBAN.FORM.CONVERSATION.NO_CONVERSATIONS') }}
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>
    </woot-modal>

    <!-- Modal de Confirmação de Exclusão -->
    <woot-modal
      :show="showDeleteConfirmation"
      :on-close="closeDeleteModal"
      :show-close-button="true"
      :close-on-backdrop-click="true"
    >
      <div class="delete-confirmation-modal">
        <div class="modal-header">
          <div class="header-content">
            <div class="title-group">
              <h1 class="modal-title">{{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.TITLE') }}</h1>
            </div>
          </div>
        </div>

        <div class="modal-content">
          <p class="modal-message">
            {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.MESSAGE') }}
          </p>
        </div>

        <div class="modal-footer">
          <div class="button-wrapper">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="closeDeleteModal"
            >
              {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.KEEP') }}
            </Button>
            <Button
              variant="solid"
              color="ruby"
              size="sm"
              @click="confirmDelete"
            >
              {{ $t('SCHEDULE_MESSAGES.DELETE_MODAL.CONFIRM') }}
            </Button>
          </div>
        </div>
      </div>
    </woot-modal>

    <!-- Adicione isso próximo ao botão de filtro -->
    <context-menu
      :show="showFilterMenu"
      :x="filterMenuPosition.x"
      :y="filterMenuPosition.y"
      :items="filterMenuItems"
      :selected-id="statusFilter"
      @select="handleFilterSelect"
      @close="closeFilterMenu"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ConversationApi from '../../../../api/inbox/conversation';
import AccountScheduledMessagesAPI from '../../../../api/accountScheduledMessages';
import FunnelAPI from '../../../../api/funnel';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import ContextMenu from '../../../ui/ContextMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    ContextMenu,
    Button,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
    editingMessage: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      title: '',
      message: '',
      selectedInbox: '',
      scheduledTime: '',
      isLoading: false,
      scheduledMessages: [],
      isScheduledMessagesOpen: true,
      isEditing: false,
      editingMessageId: null,
      enabledFeatures: {},
      showDeleteConfirmation: false,
      messageToDelete: null,
      expandedMessages: [], // Array para controlar quais mensagens estão expandidas
      statusFilter: 'all', // Novo estado para o filtro
      showFilterMenu: false,
      filterMenuPosition: { x: 0, y: 0 },
      isRecurrent: false,
      selectedPeriod: '',
      // Novos estados para templates
      templates: [],
      selectedTemplate: null,
      loadingTemplates: false,
      attachments: [],
      existingAttachments: [],
      conditions: {
        noContactReply: true,
        conversationOpen: true,
        sameKanbanStage: false,
      },
      // Busca de conversas quando abre sem conversationId
      selectedConversationId: null,
      conversationsForSelect: [],
      conversationSearch: '',
      conversationDropdownOpen: false,
      conversationDropdownStyle: { top: '0px', left: '0px', width: '100%' },
      loadingConversations: false,
    };
  },

  computed: {
    effectiveConversationId() {
      return this.conversationId || this.selectedConversationId;
    },

    filteredConversationsForSelect() {
      if (!this.conversationSearch.trim()) return this.conversationsForSelect;
      const q = this.conversationSearch.trim().toLowerCase();
      return this.conversationsForSelect.filter(c => {
        const sender = c.meta?.sender;
        const text = `${c.display_id || c.id} ${sender?.name || ''} ${sender?.email || ''}`.toLowerCase();
        return text.includes(q);
      });
    },

    selectedConversationForSchedule() {
      const id = this.effectiveConversationId;
      return id
        ? this.conversationsForSelect.find(
            c => String(c.display_id || c.id) === String(id)
          )
        : null;
    },

    currentKanbanItem() {
      const items =
        this.currentChat?.kanban_items ||
        this.selectedConversationForSchedule?.kanban_items ||
        [];
      return Array.isArray(items) && items.length ? items[0] : null;
    },

    ...mapGetters({
      currentChat: 'getSelectedChat',
      inboxes: 'inboxes/getInboxes',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      selectedFunnel: 'funnel/getSelectedFunnel',
    }),

    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },

    isFeatureEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.SCHEDULE_MESSAGES
      );
    },

    filteredMessages() {
      if (this.statusFilter === 'all') {
        return this.scheduledMessages;
      }
      return this.scheduledMessages.filter(message => {
        const st = message.status || 'pending';
        return st === this.statusFilter;
      });
    },

    canSubmit() {
      return (
        this.message.trim().length > 0 ||
        this.attachments.length > 0 ||
        this.existingAttachments.length > 0
      );
    },

    filterMenuItems() {
      return [
        {
          id: 'all',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.ALL'),
          icon: 'list',
        },
        {
          id: 'pending',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.PENDING'),
          icon: 'clock',
        },
        {
          id: 'sent',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.SENT'),
          icon: 'checkmark',
        },
        {
          id: 'skipped',
          label: this.$t('SCHEDULE_MESSAGES.FILTER.SKIPPED'),
          icon: 'dismiss',
        },
      ];
    },
  },

  watch: {
    editingMessage(newMessage) {
      if (newMessage && this.show) {
        this.handleEdit(newMessage);
      }
    },
    show(isVisible) {
      if (isVisible && this.editingMessage) {
        this.handleEdit(this.editingMessage);
      }
      if (isVisible) {
        if (!this.conversationId) {
          this.fetchConversationsForSelect();
        }
        this.fetchTemplates();
        if (this.effectiveConversationId) {
          this.fetchScheduledMessages();
        }
      }
      if (!isVisible) {
        this.resetForm();
        this.selectedConversationId = null;
      }
    },

    conversationId() {
      if (this.show && this.effectiveConversationId) {
        this.fetchScheduledMessages();
      }
    },
  },

  async mounted() {
    document.addEventListener('mousedown', this.closeConversationDropdown);

    if (this.effectiveConversationId) await this.fetchScheduledMessages();
    await this.fetchTemplates();
    if (!this.isFeatureEnabled) {
      this.onClose();
    }

    if (this.editingMessage) {
      this.handleEdit(this.editingMessage);
    }
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.closeConversationDropdown);
  },

  methods: {
    // Nova função para buscar templates
    async fetchTemplates() {
      try {
        this.loadingTemplates = true;
        const response = await FunnelAPI.get();
        const allTemplates = [];

        response.data.forEach(funnel => {
          Object.values(funnel.stages).forEach(stage => {
            if (stage.message_templates) {
              allTemplates.push(...stage.message_templates);
            }
          });
        });

        this.templates = allTemplates;
        console.log(
          '[ScheduleMessageModal] Templates carregados:',
          this.templates
        );
      } catch (error) {
        console.error(
          '[ScheduleMessageModal] Erro ao carregar templates:',
          error
        );
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD_TEMPLATES'), 'error');
      } finally {
        this.loadingTemplates = false;
      }
    },

    // Função para aplicar template selecionado
    applyTemplate() {
      if (this.selectedTemplate) {
        this.message = this.selectedTemplate.content;
        this.title =
          this.selectedTemplate.title ||
          this.selectedTemplate.content.substring(0, 50) + '...';
      }
    },

    initializeEnabledFeatures() {
      this.enabledFeatures = this.account.features;
    },

    async fetchScheduledMessages() {
      if (!this.effectiveConversationId) return;
      try {
        const cid = String(this.effectiveConversationId);
        const response = await AccountScheduledMessagesAPI.get();
        const all = response.data?.payload || [];
        this.scheduledMessages = all.filter(
          m => String(m.conversation_display_id) === cid
        );
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
      }
    },

    onClose() {
      this.selectedConversationId = null;
      this.$emit('close');
    },

    async fetchConversationsForSelect() {
      try {
        this.loadingConversations = true;
        const all = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await ConversationApi.get({ page });
          const payload = response.data?.data?.payload ?? [];
          all.push(...payload);
          hasMore = payload.length >= 25;
          page += 1;
        }
        this.conversationsForSelect = all;
      } catch (err) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.LOAD'), 'error');
      } finally {
        this.loadingConversations = false;
      }
    },

    openConversationDropdown() {
      this.conversationDropdownOpen = true;
      this.conversationSearch = '';
      this.$nextTick(() => {
        const selector = this.$refs.conversationSelectorRef;
        if (selector) {
          const rect = selector.getBoundingClientRect();
          this.conversationDropdownStyle = {
            position: 'fixed',
            top: `${rect.bottom + window.scrollY}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: 99999,
          };
        }
        const input = selector?.querySelector('input');
        if (input) input.focus();
      });
    },

    selectConversationForSchedule(conversation) {
      this.selectedConversationId = conversation.display_id || conversation.id;
      this.conversationDropdownOpen = false;
      this.fetchScheduledMessages();
    },

    closeConversationDropdown(e) {
      if (this.$refs.conversationSelectorRef && !this.$refs.conversationSelectorRef.contains(e.target)) {
        this.conversationDropdownOpen = false;
      }
    },

    resetForm() {
      console.log('Iniciando reset do formulário');
      this.title = '';
      this.message = '';
      this.selectedInbox = '';
      this.scheduledTime = '';
      this.isLoading = false;
      this.isEditing = false;
      this.editingMessageId = null;
      this.isRecurrent = false;
      this.selectedPeriod = '';
      this.selectedTemplate = null;
      this.attachments = [];
      this.existingAttachments = [];
      this.conditions = {
        noContactReply: true,
        conversationOpen: true,
        sameKanbanStage: false,
      };
      if (this.$refs.attachmentsInput) {
        this.$refs.attachmentsInput.value = '';
      }
      console.log('Formulário resetado');
    },

    async onSubmit() {
      if (!this.effectiveConversationId) return;
      if (!this.canSubmit) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.EMPTY_MESSAGE'), 'error');
        return;
      }
      try {
        this.isLoading = true;
        const data = {
          scheduled_message: {
            inbox_id: this.selectedInbox,
            content: this.message,
            scheduled_at: new Date(this.scheduledTime).toISOString(),
            title: this.title,
            is_recurrent: this.isRecurrent,
            period: this.selectedPeriod,
            conditions: this.buildConditionsPayload(),
            attachments: this.attachments,
          },
        };

        await ConversationApi.scheduleMessage({
          conversation_id: this.effectiveConversationId,
          ...data.scheduled_message,
        });

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.CREATE'), 'success');
        await this.fetchScheduledMessages();
        this.resetForm();
        this.onClose();
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.CREATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async updateScheduledMessage() {
      if (!this.canSubmit) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.EMPTY_MESSAGE'), 'error');
        return;
      }
      try {
        this.isLoading = true;
        const data = {
          inbox_id: this.selectedInbox,
          content: this.message,
          scheduled_at: new Date(this.scheduledTime).toISOString(),
          title: this.title,
          is_recurrent: this.isRecurrent,
          period: this.selectedPeriod,
          conditions: this.buildConditionsPayload(),
          attachments: this.attachments,
        };

        await ConversationApi.updateScheduledMessage(
          this.effectiveConversationId,
          this.editingMessageId,
          data
        );

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.UPDATE'), 'success');
        await this.fetchScheduledMessages();
        this.resetForm();
        this.onClose();
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.UPDATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    onAttachmentChange(event) {
      this.attachments = Array.from(event.target.files || []);
    },

    removeAttachment(index) {
      this.attachments.splice(index, 1);
      if (this.attachments.length === 0 && this.$refs.attachmentsInput) {
        this.$refs.attachmentsInput.value = '';
      }
    },

    formatFileSize(size) {
      if (!size) return '0 KB';
      if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },

    buildConditionsPayload() {
      return {
        no_contact_reply: this.conditions.noContactReply,
        conversation_open: this.conditions.conversationOpen,
        same_kanban_stage:
          this.conditions.sameKanbanStage && Boolean(this.currentKanbanItem),
        funnel_id: this.currentKanbanItem?.funnel_id || null,
        kanban_stage: this.currentKanbanItem?.funnel_stage || null,
      };
    },

    parseConditionsPayload(conditions = {}) {
      return {
        noContactReply: conditions.no_contact_reply === true,
        conversationOpen: conditions.conversation_open === true,
        sameKanbanStage: conditions.same_kanban_stage === true,
      };
    },

    hasSmartConditions(message) {
      const conditions = message.conditions || {};
      return Boolean(
        conditions.no_contact_reply ||
          conditions.conversation_open ||
          conditions.same_kanban_stage
      );
    },

    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    getInboxName(inboxId) {
      const inbox = this.inboxes.find(i => i.id === inboxId);
      return inbox ? inbox.name : this.$t('SCHEDULE_MESSAGES.ERROR.INBOX_NOT_FOUND');
    },

    getContactDisplayName(msg) {
      const name = msg?.contact_name;
      const email = msg?.contact_email;
      const fallback = this.$t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
      const text = name || email || fallback;
      return text.length > 25 ? `${text.substring(0, 25)}...` : text;
    },

    goToConversation(msg) {
      if (!msg?.conversation_display_id || !this.accountId) return;
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.accountId,
          inboxId: msg.inbox_id,
          conversation_id: msg.conversation_display_id,
        },
      });
    },

    getStatusText(status) {
      if (status === 'sent') return this.$t('SCHEDULE_MESSAGES.STATUS.SENT');
      if (status === 'skipped') {
        return this.$t('SCHEDULE_MESSAGES.STATUS.SKIPPED');
      }
      if (status === 'failed') return this.$t('SCHEDULE_MESSAGES.STATUS.FAILED');
      return this.$t('SCHEDULE_MESSAGES.STATUS.PENDING');
    },

    getStatusClass(status) {
      return {
        'status-pending': status === 'pending',
        'status-sent': status === 'sent',
        'status-skipped': status === 'skipped',
      };
    },

    handleEdit(message) {
      console.log('Preenchendo formulário para edição:', message);

      this.title = message.title;
      this.message = message.message;
      this.selectedInbox = message.inbox_id;
      this.attachments = [];
      this.existingAttachments = message.attachments || [];
      this.conditions = this.parseConditionsPayload(message.conditions);
      if (this.$refs.attachmentsInput) {
        this.$refs.attachmentsInput.value = '';
      }

      // Converte o timestamp para o formato datetime-local
      const date = new Date(message.scheduled_at * 1000);
      this.scheduledTime = date.toISOString().slice(0, 16);

      // Carrega dados de recorrência
      this.isRecurrent = message.is_recurrent || false;
      if (
        message.period &&
        typeof message.period === 'object' &&
        message.period.type
      ) {
        this.selectedPeriod = message.period.type;
      } else if (typeof message.period === 'string') {
        this.selectedPeriod = message.period;
      } else {
        this.selectedPeriod = '';
      }

      // Limpa template selecionado na edição
      this.selectedTemplate = null;

      this.isEditing = true;
      this.editingMessageId = message.id;
      this.isScheduledMessagesOpen = false;
    },

    async deleteMessage(id) {
      this.messageToDelete = id;
      this.showDeleteConfirmation = true;
    },

    closeDeleteModal() {
      this.showDeleteConfirmation = false;
      this.messageToDelete = null;
    },

    async confirmDelete() {
      try {
        await ConversationApi.deleteScheduledMessage(
          this.effectiveConversationId,
          this.messageToDelete
        );
        await this.fetchScheduledMessages();

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.DELETE'), 'success');
      } catch (error) {
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.DELETE'), 'error');
      } finally {
        this.closeDeleteModal();
      }
    },

    toggleScheduledMessages() {
      this.isScheduledMessagesOpen = !this.isScheduledMessagesOpen;
    },

    handleSubmit(event) {
      if (!this.isEditing) {
        this.onSubmit();
      }
    },

    toggleMessageExpand(messageId) {
      const index = this.expandedMessages.indexOf(messageId);
      if (index === -1) {
        this.expandedMessages.push(messageId);
      } else {
        this.expandedMessages.splice(index, 1);
      }
    },

    openFilterMenu(event) {
      event.stopPropagation();
      const rect = event.target.getBoundingClientRect();
      this.filterMenuPosition = {
        x: rect.left,
        y: rect.bottom + 4,
      };
      this.showFilterMenu = true;
    },

    closeFilterMenu() {
      this.showFilterMenu = false;
    },

    handleFilterSelect(item) {
      this.statusFilter = item.id;
      this.closeFilterMenu();
    },

    async handleResend(message) {
      try {
        this.isLoading = true;

        // Calcula 5 segundos a partir do horário atual
        const now = new Date();
        const scheduledTime = new Date(now.getTime() + 5000); // 5 segundos

        const data = {
          inbox_id: message.inbox_id,
          content: message.message,
          scheduled_at: scheduledTime.toISOString(),
          title: message.title,
        };

        // Se a mensagem original era recorrente, mantém a recorrência
        if (message.is_recurrent && message.period) {
          data.is_recurrent = message.is_recurrent;
          data.period = message.period;
        }

        await ConversationApi.scheduleMessage({
          conversation_id: this.effectiveConversationId,
          ...data,
        });

        useAlert(this.$t('SCHEDULE_MESSAGES.SUCCESS.RESEND'), 'success');
        await this.fetchScheduledMessages();
      } catch (error) {
        console.error('Erro ao reenviar mensagem:', error);
        useAlert(this.$t('SCHEDULE_MESSAGES.ERROR.CREATE'), 'error');
      } finally {
        this.isLoading = false;
      }
    },

    getPeriodText(period) {
      // Se period for um objeto JSON, extrai o tipo
      const periodType =
        typeof period === 'object' && period !== null ? period.type : period;

      switch (periodType) {
        case 'every_day':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY');
        case 'every_day_at_time':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY_AT_TIME');
        case 'every_week':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEK');
        case 'every_month':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_MONTH');
        case 'every_weekday':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKDAY');
        case 'every_weekend':
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_WEEKEND');
        default:
          return this.$t('SCHEDULE_MESSAGES.FREQUENCY.EVERY_DAY');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.schedule-message-container {
  // Se necessário, adicione estilos específicos para o container
}

.schedule-message {
  display: flex;
  gap: 24px;
  width: min(90vw, 1200px);
  min-width: 800px;
  height: 80vh;

  .form-column {
    width: 45%;
    min-width: 380px;
    padding: 0 24px 24px;
    border-right: 1px solid;
    overflow-y: auto;
    @apply border-slate-100 dark:border-slate-700;
  }

  .scheduled-list-column {
    width: 55%;
    min-width: 420px;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
}

.scheduled-messages {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scheduled-messages-list {
  flex: 1;
  overflow-y: auto;
  margin-top: 12px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    @apply dark:bg-slate-600;
  }

  scrollbar-width: thin;

  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  .dark & {
    scrollbar-color: rgb(71, 85, 105) transparent;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease-out;
  max-height: 1000px;
  opacity: 1;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.message-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.status-pending {
    background-color: #fef3c7;
    color: #92400e;

    .status-dot {
      background-color: #92400e;
    }

    @apply dark:bg-yellow-900 dark:text-yellow-200;

    .status-dot {
      @apply dark:bg-yellow-200;
    }
  }

    &.status-sent {
      background-color: #d1fae5;
      color: #065f46;

    .status-dot {
      background-color: #065f46;
    }

    @apply dark:bg-green-900 dark:text-green-200;

    .status-dot {
      @apply dark:bg-green-200;
    }
  }

  &.status-skipped {
    background-color: #e2e8f0;
    color: #475569;

    .status-dot {
      background-color: #475569;
    }

    @apply dark:bg-slate-700 dark:text-slate-200;
  }
}

.scheduled-message {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .message-header {
    padding: 12px 14px;
    cursor: pointer;

    .header-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .title-section {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .title-and-badge {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .title {
        font-weight: 500;
        font-size: 13px;
        color: #1a1a1a;
        @apply dark:text-slate-100;
      }

      .expand-icon {
        color: #666;
        @apply dark:text-slate-400;
      }
    }

    .meta-info {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 12px;
      color: #666;
      @apply dark:text-slate-400;

      .datetime,
      .inbox,
      .recurrence {
        display: flex;
        align-items: center;
        gap: 4px;

        .icon {
          color: #1f93ff;
        }
      }

      .recurrence {
        .icon {
          color: #10b981;
        }

        .recurrence-text {
          font-weight: 500;
          color: #10b981;
          @apply dark:text-green-400;
        }
      }
    }
  }

  .message-expandable {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    @apply dark:border-slate-700;
    animation: slideDown 0.2s ease-out;
  }

  .message-preview {
    padding: 12px;
    background-color: rgba(0, 0, 0, 0.02);
    @apply dark:bg-slate-700/30;

    .preview-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
      color: #666;
      @apply dark:text-slate-300;

      .preview-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #1f93ff;

        svg {
          width: 14px;
          height: 14px;
        }
      }
    }

    .preview-content {
      padding-left: 20px;
      font-size: 13px;
      line-height: 1.4;
      color: #4a4a4a;
      @apply dark:text-slate-200;
      white-space: pre-wrap;
    }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px;
    background-color: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);

    .action-button {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }

      &.resend {
        color: #34d399;
      }

      &.edit {
        color: #60a5fa;
      }

      &.open-chat {
        color: #64748b;

        @apply dark:text-slate-300;
      }

      &.delete {
        color: #ff7875;
      }
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &.status-pending {
      background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
      color: #92400e;

      .pulse-dot {
        background-color: currentColor;
      }

      @apply dark:bg-yellow-900/30 dark:text-yellow-300;
    }

    &.status-sent {
      background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
      color: #065f46;

      .pulse-dot {
        background-color: currentColor;
      }

      @apply dark:bg-green-900/30 dark:text-green-300;
    }

    &.status-skipped {
      background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
      color: #475569;

      .pulse-dot {
        background-color: currentColor;
      }

      @apply dark:bg-slate-800 dark:text-slate-300;
    }

    .pulse-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.4);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark {
  .scheduled-message {
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
    border-color: rgba(255, 255, 255, 0.05);

    .actions {
      background-color: #1e293b;
      border-color: rgba(255, 255, 255, 0.1);

      .action-button {
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        &.resend {
          color: #34d399;
        }

        &.edit {
          color: #60a5fa;
        }

        &.open-chat {
          color: #94a3b8;
        }

        &.delete {
          color: #ff7875;
        }
      }
    }

    .status-badge {
      &.status-pending {
        background: linear-gradient(
          135deg,
          rgba(234, 179, 8, 0.2) 0%,
          rgba(234, 179, 8, 0.3) 100%
        );
        color: #fbbf24;
      }

      &.status-sent {
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.2) 0%,
          rgba(16, 185, 129, 0.3) 100%
        );
        color: #34d399;
      }

      &.status-skipped {
        background: linear-gradient(
          135deg,
          rgba(148, 163, 184, 0.2) 0%,
          rgba(148, 163, 184, 0.3) 100%
        );
        color: #cbd5e1;
      }
    }
  }
}

::v-deep .modal-container {
  width: auto !important;
  min-width: auto !important;
  max-width: none !important;
  margin: 2rem;
}

.scheduled-messages-header {
  margin-top: 8px;

  .header-content {
    .title-group {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: 8px;

      h3 {
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;

        .counter {
          @apply bg-woot-500 text-white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          min-width: 24px;
          text-align: center;
          @apply dark:bg-woot-500 dark:text-white;
        }
      }

      .filter-button {
        padding: 4px;
        border-radius: 4px;
        color: #666;
        @apply dark:text-slate-400;

        &:hover {
          @apply bg-slate-100 dark:bg-slate-700;
        }
      }
    }
  }
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid;
  @apply border-slate-100 dark:border-slate-700;

  .header-content {
    display: flex;
    align-items: center;
    height: 32px;

    .title-group {
      .modal-title {
        font-size: 18px;
        font-weight: 600;
        @apply text-slate-900 dark:text-slate-50;
        white-space: nowrap;
      }
    }
  }
}

.form-group {
  input[type='datetime-local'] {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid;
    @apply border-slate-200 dark:border-slate-600;
    border-radius: 4px;
    font-size: 14px;
    @apply bg-white dark:bg-slate-700;
    @apply text-slate-900 dark:text-slate-100;

    &:focus {
      outline: none;
      @apply border-woot-500;
      box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.1);
    }

    &::-webkit-calendar-picker-indicator {
      filter: invert(0.5);
      @apply dark:invert;
      cursor: pointer;
      opacity: 0.6;

      &:hover {
        opacity: 1;
      }
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid;
    @apply border-slate-200 dark:border-slate-600;
    border-radius: 4px;
    font-size: 14px;
    @apply bg-white dark:bg-slate-700;
    @apply text-slate-900 dark:text-slate-100;

    &:focus {
      outline: none;
      @apply border-woot-500;
      box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.1);
    }
  }

  .checkbox-group {
    margin-top: 8px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;

    .checkbox-input {
      width: 16px;
      height: 16px;
      accent-color: #10b981;
      cursor: pointer;
    }

    .checkbox-text {
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.modal-footer {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid;
  @apply border-slate-100 dark:border-slate-600;

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    gap: 8px;

    button {
      min-width: 80px;
    }
  }
}

.delete-confirmation-modal {
  padding: 1.5rem;

  .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    @apply text-slate-900 dark:text-slate-100;
  }

  .modal-message {
    @apply text-slate-700 dark:text-slate-300;
  }

  .modal-footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid;
    @apply border-slate-100 dark:border-slate-700;

    .button-wrapper {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;

  .empty-state-content {
    text-align: center;

    .empty-icon {
      display: block;
      margin: 0 auto 16px auto;
      @apply text-n-blue-10;
    }

    .empty-text {
      font-size: 14px;
      font-weight: 500;
      margin-bottom: 8px;
      @apply text-slate-700 dark:text-slate-300;
    }

    .empty-subtext {
      font-size: 13px;
      @apply text-slate-500 dark:text-slate-400;
    }
  }
}

// Estilos para o template selector
.template-selector {
  position: relative;

  .template-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 12px;
    @apply text-slate-500 dark:text-slate-400;

    .loading-spinner {
      width: 12px;
      height: 12px;
      border: 2px solid;
      border-top-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      @apply border-slate-300 dark:border-slate-600;
    }

    .loading-text {
      font-size: 11px;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
